<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

/**
 * Body of an action_params entry for `update_contact`.
 * Shape produced/consumed:
 *   [{ field_key: String, field_type: 'standard'|'custom_attribute', value: String }]
 */
const props = defineProps({
  modelValue: {
    type: [Array, Object, String],
    default: () => [],
  },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const store = useStore();

const STANDARD_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone_number', label: 'Phone Number' },
  { key: 'identifier', label: 'Identifier' },
];

const liquidHints = [
  '{{contact.name}}',
  '{{contact.email}}',
  '{{contact.phone_number}}',
  '{{conversation.id}}',
  '{{inbox.name}}',
];

const value = computed(() => {
  if (Array.isArray(props.modelValue) && props.modelValue[0]) {
    return props.modelValue[0];
  }
  return {};
});

const contactCustomAttributes = computed(() => {
  const all = store.getters['attributes/getAttributes'] || [];
  return all.filter(a => a.attribute_model === 'contact_attribute');
});

// composite key combining type + key; lets us distinguish standard vs custom
function compositeKey(type, key) {
  return `${type}::${key}`;
}

const selectedComposite = computed({
  get: () => {
    if (!value.value.field_key) return null;
    return compositeKey(
      value.value.field_type || 'standard',
      value.value.field_key
    );
  },
  set: composite => {
    if (!composite) {
      emit('update:modelValue', [{}]);
      return;
    }
    const [field_type, field_key] = composite.split('::');
    emit('update:modelValue', [{ ...value.value, field_type, field_key }]);
  },
});

const fieldValue = computed({
  get: () => value.value.value || '',
  set: v => emit('update:modelValue', [{ ...value.value, value: v }]),
});
</script>

<template>
  <div class="w-full mt-2 flex flex-col gap-2">
    <div>
      <label class="text-xs text-n-slate-11 mb-1 block">
        {{ t('AUTOMATION.ACTION.UPDATE_CONTACT.FIELD_LABEL') }}
      </label>
      <select v-model="selectedComposite" class="m-0 w-full">
        <option :value="null" disabled>
          {{ t('AUTOMATION.ACTION.UPDATE_CONTACT.FIELD_PLACEHOLDER') }}
        </option>
        <optgroup :label="t('AUTOMATION.ACTION.UPDATE_CONTACT.STANDARD_GROUP')">
          <option
            v-for="field in STANDARD_FIELDS"
            :key="field.key"
            :value="compositeKey('standard', field.key)"
          >
            {{ field.label }}
          </option>
        </optgroup>
        <optgroup
          v-if="contactCustomAttributes.length"
          :label="t('AUTOMATION.ACTION.UPDATE_CONTACT.CUSTOM_GROUP')"
        >
          <option
            v-for="attr in contactCustomAttributes"
            :key="attr.attribute_key"
            :value="compositeKey('custom_attribute', attr.attribute_key)"
          >
            {{ attr.attribute_display_name || attr.attribute_key }}
          </option>
        </optgroup>
      </select>
    </div>
    <div v-if="selectedComposite">
      <label class="text-xs text-n-slate-11 mb-1 block">
        {{ t('AUTOMATION.ACTION.UPDATE_CONTACT.VALUE_LABEL') }}
      </label>
      <input
        v-model="fieldValue"
        type="text"
        class="answer--text-input mb-0 w-full"
        :placeholder="t('AUTOMATION.ACTION.UPDATE_CONTACT.VALUE_PLACEHOLDER')"
      />
      <p class="text-xs text-n-slate-10 mt-1">
        {{ t('AUTOMATION.ACTION.UPDATE_CONTACT.LIQUID_HINT') }}
        <code
          v-for="hint in liquidHints"
          :key="hint"
          class="text-xs px-1 mx-0.5 bg-n-alpha-2 rounded"
        >
          {{ hint }}
        </code>
      </p>
    </div>
  </div>
</template>
